import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { trackContactFormSubmission } from './Analytics';
import { API_URL as API } from '../config/api';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', organisation: '',
    enquiry_type: '', location: '', requirement: '', message: '',
  });
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    axios.get(`${API}/settings`).then(r => {
      if (r.data?.contact_enabled === false) setEnabled(false);
    }).catch(() => {});
  }, []);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in the required fields');
      return;
    }
    if (!consent) {
      toast.error('Please tick the consent box');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        service: formData.enquiry_type || 'OTHER',
        message: [
          formData.enquiry_type && `Type: ${formData.enquiry_type}`,
          formData.phone && `Phone: ${formData.phone}`,
          formData.organisation && `Organisation: ${formData.organisation}`,
          formData.location && `Location: ${formData.location}`,
          formData.requirement && `Requirement: ${formData.requirement}`,
          formData.message,
        ].filter(Boolean).join('\n'),
      });
      setIsSubmitted(true);
      toast.success('Your enquiry has been sent. We will reply personally.');
      trackContactFormSubmission(formData.enquiry_type);
      setFormData({ name: '', email: '', phone: '', organisation: '', enquiry_type: '', location: '', requirement: '', message: '' });
      setConsent(false);
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!enabled) return null;

  return (
    <section className="close" id="contact" data-testid="contact-section">
      <div className="wrap c-grid">
        {/* Left side */}
        <div className="side">
          <span className="eyebrow">Get in touch</span>
          <h2 data-testid="contact-headline">Discuss <em>a requirement.</em></h2>
          <p>A few lines is enough: who needs accommodation or what the property is, where, and for how long. We will come back to you personally.</p>
          <div className="contact-lines">
            <a href="mailto:info@aureate-resident.com" data-testid="contact-email">info@aureate-resident.com</a>
            <a href="tel:+447759342229" data-testid="contact-phone">+44 7759 342229</a>
            <a href="tel:+441313811989">+44 131 381 1989</a>
            <span>Edinburgh, United Kingdom</span>
          </div>
        </div>

        {/* Right - form */}
        <form className="ref-form" id="enquiry" onSubmit={handleSubmit} data-testid="contact-form">
          <div className="f">
            <label htmlFor="nm">Name</label>
            <input id="nm" name="name" autoComplete="name" required value={formData.name} onChange={handleChange} disabled={isSubmitting} data-testid="contact-input-name" />
          </div>
          <div className="f">
            <label htmlFor="em">Email</label>
            <input id="em" name="email" type="email" autoComplete="email" required value={formData.email} onChange={handleChange} disabled={isSubmitting} data-testid="contact-input-email" />
          </div>
          <div className="f">
            <label htmlFor="ph">Phone (optional)</label>
            <input id="ph" name="phone" type="tel" autoComplete="tel" value={formData.phone} onChange={handleChange} disabled={isSubmitting} />
          </div>
          <div className="f">
            <label htmlFor="org">Organisation (optional)</label>
            <input id="org" name="organisation" autoComplete="organization" value={formData.organisation} onChange={handleChange} disabled={isSubmitting} />
          </div>
          <div className="f full">
            <label htmlFor="who">I am</label>
            <select id="who" name="enquiry_type" value={formData.enquiry_type} onChange={handleChange} disabled={isSubmitting} data-testid="contact-input-service">
              <option value="">Choose one</option>
              <option value="ORGANISATION">An organisation needing accommodation</option>
              <option value="LANDLORD">A landlord or property owner</option>
              <option value="COMMERCIAL">An owner of commercial property</option>
              <option value="OTHER">Something else</option>
            </select>
          </div>
          <div className="f">
            <label htmlFor="loc">Where?</label>
            <input id="loc" name="location" placeholder="Town or postcode" value={formData.location} onChange={handleChange} disabled={isSubmitting} />
          </div>
          <div className="f">
            <label htmlFor="size">How many people, how long?</label>
            <input id="size" name="requirement" placeholder="If you need accommodation" value={formData.requirement} onChange={handleChange} disabled={isSubmitting} />
          </div>
          <div className="f full">
            <label htmlFor="msg">Tell us about it</label>
            <textarea id="msg" name="message" value={formData.message} onChange={handleChange} disabled={isSubmitting} data-testid="contact-input-message" />
          </div>
          <label className="consent" htmlFor="ok">
            <input id="ok" type="checkbox" required checked={consent} onChange={(e) => setConsent(e.target.checked)} disabled={isSubmitting} data-testid="contact-consent" />
            <span>I agree to Aureate Residences using these details to reply to my enquiry, as set out in the <a href="/privacy-policy">privacy policy</a>.</span>
          </label>
          <button className="btn btn-gold" type="submit" disabled={isSubmitting || isSubmitted} data-testid="contact-submit-button">
            {isSubmitting ? 'Sending...' : isSubmitted ? 'Sent' : 'Send requirement \u2192'}
          </button>
          <p className="reply">We reply to every enquiry within two working days.</p>
        </form>
      </div>
    </section>
  );
};

export default ContactSection;
